import React, { useState, useEffect } from "react";
import {
	XMarkIcon,
	UserGroupIcon,
	ArrowLongRightIcon,
} from "@heroicons/react/24/outline";
import { useEventStore } from "../../../store/eventStore";

export default function PrepairAssignModal({
	isOpen,
	onClose,
	searchType,
	eventData,
	trainData,
	engineData,
	onPrepare,
}) {
	// API 관련 상태
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	// 예약 정보 상태
	const [reservationList, setReservationList] = useState([]);

	// 선택된 항목 상태
	const [selectedItems, setSelectedItems] = useState([]);

	// 배정 인원 상태 (각 예약별 배정 인원)
	const [assignCounts, setAssignCounts] = useState({});

	const { getReservationInfo } = useEventStore();

	// 모달이 열릴 때 예약 정보 조회
	useEffect(() => {
		if (isOpen && eventData && trainData && engineData) {
			fetchReservationInfo();
		}
	}, [isOpen, eventData]);

	// 모달이 닫힐 때 모든 상태 초기화
	useEffect(() => {
		if (!isOpen) {
			setReservationList([]);
			setSelectedItems([]);
			setAssignCounts({});
			setError(null);
		}
	}, [isOpen]);

	if (!isOpen || !eventData) return null;

	// 예약 정보 조회 함수
	const fetchReservationInfo = async () => {
		setIsLoading(true);
		setError(null);

		try {
			const promises = eventData.map(async (event) => {
				const EV_YM = event.ev_cd.split("-")[0] || "";
				const EV_SEQ = event.ev_cd.split("-")[1] || "";

				const response = await getReservationInfo(
					EV_YM,
					EV_SEQ,
					searchType
				);
				return {
					event,
					reservations: response.success ? response.data : [],
				};
			});

			const results = await Promise.all(promises);
			const allReservations = results.flatMap((result) =>
				result.reservations.map((res) => ({
					...res,
					eventId: `${result.event.ev_cd}`,
					eventName: result.event.ev_nm || "-",
				}))
			);

			setReservationList(allReservations);

			// 각 예약에 대한 초기 배정 인원 설정
			const initialAssignCounts = {};

			// 이벤트별로 그룹화하여 처리
			eventData.forEach((event) => {
				const eventId = event.ev_cd;
				const eventReservations = allReservations.filter(
					(res) => res.eventId === eventId
				);

				eventReservations.forEach((reservation, index) => {
					// 이미 배정된 좌석 수 계산
					const assignedSeatsCount = reservation.SEAT_INFO_LIST
						? reservation.SEAT_INFO_LIST.length
						: 0;
					// 남은 좌석 수 계산 (총 인원 - 이미 배정된 좌석 수)
					const remainingSeats = Math.max(
						0,
						reservation.TOTAL_CNT - assignedSeatsCount
					);
					// 초기값으로 남은 좌석 수 설정 (이벤트별 인덱스 사용)
					initialAssignCounts[`${reservation.eventId}-${index}`] =
						remainingSeats;
				});
			});

			console.log("initialAssignCounts: ", initialAssignCounts);

			// 배정 인원 상태 업데이트
			setAssignCounts(initialAssignCounts);
		} catch (error) {
			console.error("예약 정보 조회 중 오류 발생:", error);
			setError("예약 정보 조회 중 오류가 발생했습니다.");
		} finally {
			setIsLoading(false);
		}
	};

	// 배정 준비 실행 핸들러
	const handlePrepare = async () => {
		// 선택된 항목이 없거나 배정 인원이 0인 경우 처리
		if (selectedItems.length === 0) {
			alert("배정할 항목을 선택해주세요.");
			return;
		}

		const totalAssignCount = Object.values(assignCounts).reduce(
			(sum, count) => sum + count,
			0
		);
		if (totalAssignCount === 0) {
			alert("배정할 인원을 입력해주세요.");
			return;
		}

		setIsLoading(true);
		try {
			// 선택된 항목과 배정 인원 정보 구성
			const assignData = selectedItems
				.map((combinedId) => {
					// 마지막 '-'를 기준으로 분리
					const lastDashIndex = combinedId.lastIndexOf("-");
					const eventId = combinedId.substring(0, lastDashIndex);
					const index = combinedId.substring(lastDashIndex + 1);

					// 해당 이벤트의 예약 목록 필터링
					const eventReservations = reservationList.filter(
						(r) => r.eventId === eventId
					);
					const item = eventReservations[parseInt(index)];
					return {
						id: combinedId,
						item: item,
						assign_count: assignCounts[combinedId] || 0,
					};
				})
				.filter((item) => item.assign_count > 0); // 배정 인원이 0보다 큰 항목만 포함

			// 배정 준비 정보를 부모 컴포넌트로 전달
			if (typeof onPrepare === "function") {
				// 이벤트 데이터에 열차와 엔진 정보 추가
				const eventDataWithTrainEngine = {
					...eventData,
					trainData,
					engineData,
				};
				await onPrepare(eventDataWithTrainEngine, assignData);
			} else {
				onClose(); // 성공 시 모달 닫기
			}
		} catch (error) {
			console.error("배정 준비 중 오류 발생:", error);
			setError("배정 준비 중 오류가 발생했습니다.");
		} finally {
			setIsLoading(false);
		}
	};

	const TrainInfoBox = (
		<div className="bg-gray-50 px-4 pt-4 pb-2 rounded-md mb-4">
			<div className="grid grid-cols-3 gap-4">
				{/* 출발일 정보 */}
				<div className="flex flex-col">
					<span className="text-xs text-gray-500 mb-1">출발일</span>
					<span className="font-medium text-gray-800">
						{eventData[0].start_day || "-"}
					</span>
				</div>

				{/* 구분 정보 */}
				<div className="flex flex-col">
					<span className="text-xs text-gray-500 mb-1">구분</span>
					<span className="font-medium text-gray-800">
						{searchType === "go" ? "왕편" : "복편"}
					</span>
				</div>

				{/* 열차 정보 */}
				<div className="flex flex-col">
					<span className="text-xs text-gray-500 mb-1">열차</span>
					<div className="flex items-center font-medium text-gray-800">
						<span>({trainData.train_code || "-"} )</span>
						<span className="text-black px-2 py-1 ml-1 rounded-md">
							{trainData.dept_stn || "-"}
						</span>
						<ArrowLongRightIcon className="h-5 w-5 mx-1 text-gray-500" />
						<span className="text-black px-2 py-1 rounded-md">
							{trainData.arrv_stn || "-"}
						</span>
					</div>
				</div>
			</div>
		</div>
	);
	return (
		<div
			className="fixed inset-0 z-50 overflow-y-auto"
			aria-labelledby="modal-title"
			role="dialog"
			aria-modal="true"
		>
			<div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
				{/* 배경 오버레이 */}
				<div
					className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
					aria-hidden="true"
					onClick={onClose}
				></div>

				{/* 모달 중앙 정렬을 위한 트릭 */}
				<span
					className="hidden sm:inline-block sm:align-middle sm:h-screen"
					aria-hidden="true"
				>
					&#8203;
				</span>

				{/* 모달 내용 */}
				<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
					{/* 모달 헤더 */}
					<div className="bg-gray-50 px-4 py-3 border-b border-gray-200 sm:px-6 flex justify-between items-center">
						<h3 className="text-lg leading-6 font-medium text-gray-900">
							배정인원 선택
						</h3>
						<button
							type="button"
							className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
							onClick={onClose}
						>
							<span className="sr-only">닫기</span>
							<XMarkIcon className="h-6 w-6" aria-hidden="true" />
						</button>
					</div>

					{/* 모달 본문 */}
					<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
						<div className="sm:flex sm:items-start">
							<div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
								{/* 행사 정보 요약 */}
								{TrainInfoBox}

								{/* 행사별 예약 정보 */}
								{Array.isArray(eventData) &&
								eventData.length > 0 ? (
									<div className="space-y-6">
										{eventData.map((event, eventIndex) => {
											// 이 행사에 해당하는 예약 목록 필터링
											const filteredReservationList =
												reservationList.filter(
													(res) =>
														res.eventId ===
														`${event.ev_cd}`
												);

											return (
												<div
													key={`event-${eventIndex}`}
													className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
												>
													{/* 행사 헤더 */}
													<div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
														<h4 className="text-medium font-medium text-gray-900 flex items-center">
															<span className="bg-blue-100 text-blue-800 text-xs font-semibold mr-3 px-2 py-1 rounded">
																#{" "}
																{eventIndex + 1}
															</span>
															{event.ev_nm ||
																"행사명 없음"}
														</h4>
														<p className="text-sm text-gray-600 mt-1">
															{event.start_day &&
															event.end_day
																? `${event.start_day} ~ ${event.end_day}`
																: "일정 정보 없음"}
														</p>
													</div>

													{/* 예약 정보 테이블 */}
													<div className="p-4">
														{filteredReservationList.length >
														0 ? (
															<div className="overflow-x-auto">
																<table className="min-w-full divide-y divide-gray-200">
																	{buildTableHeader(
																		filteredReservationList
																	)}
																	{buildTableBody(
																		filteredReservationList
																	)}
																</table>
															</div>
														) : (
															<div className="text-center py-4">
																<p className="text-gray-500">
																	이 행사에
																	대한 예약
																	정보가
																	없습니다.
																</p>
															</div>
														)}
													</div>
												</div>
											);
										})}
									</div>
								) : (
									<div className="text-center py-8">
										<p className="text-gray-500">
											행사 정보가 없습니다.
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* 모달 푸터 */}
					<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
						<button
							type="button"
							className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
							onClick={handlePrepare}
							disabled={
								isLoading ||
								selectedItems.length === 0 ||
								Object.values(assignCounts).reduce(
									(sum, count) => sum + count,
									0
								) === 0
							}
						>
							{isLoading ? (
								<>
									<svg
										className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										></circle>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									처리 중...
								</>
							) : (
								"다음"
							)}
						</button>
						<button
							type="button"
							className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
							onClick={onClose}
						>
							취소
						</button>
					</div>
				</div>
			</div>
		</div>
	);

	function buildTableHeader(filteredReservationList) {
		return (
			<thead className="bg-gray-50">
				<tr>
					<th
						scope="col"
						className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
					>
						<input
							type="checkbox"
							className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
							onChange={(e) => {
								if (e.target.checked) {
									// 이 행사의 모든 예약 선택
									setSelectedItems([
										...selectedItems,
										...filteredReservationList
											.map(
												(item, index) =>
													`${item.eventId}-${index}`
											)
											.filter(
												(id) =>
													!selectedItems.includes(id)
											),
									]);
								} else {
									// 이 행사의 모든 예약 선택 해제
									// 현재 행사의 모든 예약 ID 목록 생성
									const eventItemIds =
										filteredReservationList.map(
											(item, index) =>
												`${item.eventId}-${index}`
										);

									// 현재 행사에 속하지 않는 항목만 유지
									setSelectedItems(
										selectedItems.filter(
											(id) => !eventItemIds.includes(id)
										)
									);
								}
							}}
							checked={
								filteredReservationList.length > 0 &&
								filteredReservationList.every((item, index) =>
									selectedItems.includes(
										`${item.eventId}-${index}`
									)
								)
							}
						/>
					</th>
					<th
						scope="col"
						className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
					>
						번호
					</th>
					<th
						scope="col"
						className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
					>
						대표자명
					</th>
					<th
						scope="col"
						className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
					>
						연락처
					</th>
					<th
						scope="col"
						className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
					>
						총인원
					</th>
					<th
						scope="col"
						className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
					>
						배정인원
					</th>
					<th
						scope="col"
						className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
					>
						배정완료
					</th>
				</tr>
			</thead>
		);
	}

	function buildTableBody(filteredReservationList) {
		return (
			<tbody className="bg-white divide-y divide-gray-200">
				{filteredReservationList.map((reservation, index) => {
					const assignedCount = reservation.SEAT_INFO_LIST?.length || 0;
					const isFullyAssigned = assignedCount >= reservation.TOTAL_CNT;
					const isPartiallyAssigned = assignedCount > 0 && !isFullyAssigned;
					const isSelected = selectedItems.includes(
						reservation.eventId + "-" + index
					);

					return (
					<tr
						key={reservation.eventId + "-" + index}
						className={
							isSelected
								? "bg-blue-50"
								: isFullyAssigned
								? "bg-gray-50 opacity-60"
								: ""
						}
					>
						<td className="px-3 py-4 whitespace-nowrap">
							<input
								type="checkbox"
								className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
								checked={isSelected}
								onChange={(e) => {
									if (e.target.checked) {
										setSelectedItems([
											...selectedItems,
											`${reservation.eventId}-${index}`,
										]);
									} else {
										setSelectedItems(
											selectedItems.filter(
												(id) =>
													id !==
													`${reservation.eventId}-${index}`
											)
										);
									}
								}}
							/>
						</td>
						<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
							{index + 1}
						</td>
						<td className="px-3 py-4 whitespace-nowrap">
							<div className={`text-sm font-medium ${isFullyAssigned ? "text-gray-400" : "text-gray-900"}`}>
								{reservation.RES_NM}
							</div>
						</td>
						<td className="px-3 py-4 whitespace-nowrap">
							<div className={`text-sm ${isFullyAssigned ? "text-gray-400" : "text-gray-500"}`}>
								{reservation.HANDPHONE1 +
									"-" +
									reservation.HANDPHONE2 +
									"-" +
									reservation.HANDPHONE3}
							</div>
						</td>
						<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
							{reservation.TOTAL_CNT}
						</td>
						<td className="px-3 py-4 whitespace-nowrap">
							<input
								type="number"
								min="0"
								max={reservation.TOTAL_CNT}
								value={
									assignCounts[
										`${reservation.eventId}-${index}`
									]
								}
								onChange={(e) => {
									const value = Math.min(
										Math.max(
											0,
											parseInt(e.target.value) || 0
										),
										reservation.TOTAL_CNT -
											assignedCount
									);
									setAssignCounts({
										...assignCounts,
										[`${reservation.eventId}-${index}`]:
											value,
									});
								}}
								className={`border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-20 sm:text-sm pl-2 ${
									!isSelected || isFullyAssigned
										? "bg-gray-100"
										: ""
								}`}
								disabled={!isSelected || isFullyAssigned}
							/>
						</td>
						<td className="px-3 py-4 whitespace-nowrap text-sm">
							<span className={
								isFullyAssigned
									? "text-green-600 font-semibold"
									: isPartiallyAssigned
									? "text-orange-500 font-medium"
									: "text-gray-500"
							}>
								{assignedCount}
								{isFullyAssigned && " ✓"}
							</span>
						</td>
					</tr>
					);
				})}
			</tbody>
		);
	}
}
